import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Check, Trash2, Phone, MapPin, Mail, RotateCw, FileText, Clock, AlertTriangle, CalendarDays, List, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TYPES = ["Telefon", "Wizyta", "E-mail", "Follow-up", "Oferta", "Inne"];
const TYPE_ICON = { "Telefon": Phone, "Wizyta": MapPin, "E-mail": Mail, "Follow-up": RotateCw, "Oferta": FileText, "Inne": Clock };
const PRIO = { low: { l: "Niski", c: "text-slate-400 border-slate-500/40" }, normal: { l: "Normalny", c: "text-sky-300 border-sky-500/40" }, high: { l: "Wysoki", c: "text-rose-300 border-rose-500/40" } };
const today = () => new Date().toISOString().slice(0, 10);
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (offset = 0) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // poniedziałek = 0
  d.setDate(d.getDate() - day + offset * 7);
  return d;
};
const DAY_NAMES = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"];
const EMPTY = { title: "", type: "Telefon", customer_id: "", due_date: today(), due_time: "", priority: "normal", notes: "", employee_id: "" };

const bucketOf = (t) => {
  if (t.status === "done") return "done";
  const d = t.due_date || "";
  const tod = today();
  if (!d) return "later";
  if (d < tod) return "overdue";
  if (d === tod) return "today";
  const wk = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
  if (d <= wk) return "week";
  return "later";
};
const BUCKETS = [
  { k: "overdue", l: "Zaległe", c: "text-rose-400", icon: AlertTriangle },
  { k: "today", l: "Dziś", c: "text-amber-300", icon: Clock },
  { k: "week", l: "Ten tydzień", c: "text-sky-300", icon: Clock },
  { k: "later", l: "Później / bez terminu", c: "text-muted-foreground", icon: Clock },
  { k: "done", l: "Zrobione", c: "text-emerald-400", icon: Check },
];

export const CrmTasks = ({ isAdmin = false, customers = [], users = [], dateFrom = "", dateTo = "" }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [view, setView] = useState("list");
  const [weekOffset, setWeekOffset] = useState(0);
  const confirm = useConfirm();
  const employees = users.filter((u) => u.role === "employee" || u.role === "admin");

  const monday = useMemo(() => startOfWeek(weekOffset), [weekOffset]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; }), [monday]);
  const tasksByDate = useMemo(() => { const m = {}; tasks.forEach((t) => { if (t.due_date) (m[t.due_date] = m[t.due_date] || []).push(t); }); return m; }, [tasks]);

  const load = useCallback(async () => {
    try {
      const p = {};
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
      const { data } = await api.get("/crm/tasks", { params: p });
      setTasks(data);
    }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { overdue: [], today: [], week: [], later: [], done: [] };
    tasks.forEach((t) => g[bucketOf(t)].push(t));
    return g;
  }, [tasks]);

  const save = async () => {
    if (!edit.title.trim()) return toast.error("Podaj tytuł zadania");
    setBusy(true);
    try {
      const payload = { ...edit, customer_id: edit.customer_id || null, employee_id: isAdmin ? (edit.employee_id || null) : undefined };
      if (edit.id) await api.patch(`/crm/tasks/${edit.id}`, payload);
      else await api.post("/crm/tasks", payload);
      toast.success(edit.id ? "Zadanie zapisane" : "Zadanie dodane");
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (t) => {
    try { await api.post(`/crm/tasks/${t.id}/toggle`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => {
    if (!(await confirm({ title: "Usunąć zadanie?", description: `Usunąć „${t.title}”?`, confirmText: "Usuń" }))) return;
    try { await api.delete(`/crm/tasks/${t.id}`); toast.success("Usunięto"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const TaskRow = ({ t }) => {
    const Icon = TYPE_ICON[t.type] || Clock;
    const p = PRIO[t.priority] || PRIO.normal;
    return (
      <div data-testid={`crm-task-${t.id}`} className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${t.status === "done" ? "border-border bg-muted/30" : "border-border bg-card hover:bg-accent/30"}`}>
        <button data-testid={`crm-task-toggle-${t.id}`} onClick={() => toggle(t)} className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${t.status === "done" ? "border-emerald-500 bg-emerald-500 text-[#0D1117]" : "border-input hover:border-amber-400"}`} title="Oznacz jako zrobione">
          {t.status === "done" && <Check className="h-3.5 w-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Icon className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span className={`font-semibold ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</span>
            <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold uppercase ${p.c}`}>{p.l}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground/80">{t.type}</span>
            {t.customer_name && <span>· {t.customer_name}</span>}
            {t.due_date && <span>· {new Date(t.due_date).toLocaleDateString("pl-PL")}{t.due_time ? ` ${t.due_time}` : ""}</span>}
          </div>
          {t.notes && <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{t.notes}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button data-testid={`crm-task-edit-${t.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit({ ...EMPTY, ...t, customer_id: t.customer_id || "", employee_id: t.employee_id || "" })}><FileText className="h-4 w-4" /></Button>
          <Button data-testid={`crm-task-del-${t.id}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={() => del(t)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  };

  if (loading) return <div className="p-8 text-center font-medium text-muted-foreground">Ładowanie…</div>;

  const CalChip = ({ t }) => {
    const p = PRIO[t.priority] || PRIO.normal;
    return (
      <div data-testid={`crm-cal-task-${t.id}`} className={`group flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] bg-card/60 ${p.c} ${t.status === "done" ? "opacity-60" : ""}`}>
        <button data-testid={`crm-cal-toggle-${t.id}`} onClick={() => toggle(t)} className="shrink-0" title="Oznacz jako zrobione">
          <Check className={`h-3 w-3 ${t.status === "done" ? "text-emerald-400" : "text-muted-foreground"}`} />
        </button>
        <button onClick={() => setEdit({ ...EMPTY, ...t, customer_id: t.customer_id || "", employee_id: t.employee_id || "" })} className={`min-w-0 flex-1 truncate text-left ${t.status === "done" ? "line-through" : ""}`} title={t.title}>
          {t.due_time && <span className="font-mono text-foreground/70">{t.due_time} </span>}{t.title}
        </button>
      </div>
    );
  };

  return (
    <div data-testid="crm-tasks" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-border p-0.5">
            <button data-testid="crm-view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "list" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}><List className="h-3.5 w-3.5" />Lista</button>
            <button data-testid="crm-view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "calendar" ? "bg-amber-400 text-[#0D1117]" : "text-muted-foreground hover:text-foreground"}`}><CalendarDays className="h-3.5 w-3.5" />Kalendarz</button>
          </div>
          {view === "list" && (
            <label className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Checkbox data-testid="crm-tasks-showdone" checked={showDone} onCheckedChange={(v) => setShowDone(!!v)} /> Pokaż zrobione
            </label>
          )}
        </div>
        <Button data-testid="crm-task-add" onClick={() => setEdit({ ...EMPTY })} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Plus className="h-4 w-4" />Nowe zadanie</Button>
      </div>

      {view === "calendar" && (
        <div data-testid="crm-calendar">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" data-testid="crm-cal-prev" onClick={() => setWeekOffset((w) => w - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" className="h-8" data-testid="crm-cal-today" onClick={() => setWeekOffset(0)}>Dziś</Button>
              <Button variant="outline" size="icon" className="h-8 w-8" data-testid="crm-cal-next" onClick={() => setWeekOffset((w) => w + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <div data-testid="crm-cal-range" className="font-mono text-xs font-bold uppercase tracking-widest text-amber-300">
              {monday.toLocaleDateString("pl-PL", { day: "numeric", month: "short" })} – {weekDays[6].toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" })}
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="grid grid-cols-7 gap-2 min-w-[760px]">
              {weekDays.map((d, idx) => {
                const key = ymd(d);
                const list = (tasksByDate[key] || []).slice().sort((a, b) => (a.due_time || "").localeCompare(b.due_time || ""));
                const isToday = key === today();
                return (
                  <div key={key} data-testid={`crm-cal-day-${key}`} className={`rounded-xl border p-2 min-h-[160px] ${isToday ? "border-amber-400/60 bg-amber-400/[0.04]" : "border-border bg-card"}`}>
                    <div className={`mb-2 flex items-baseline justify-between ${isToday ? "text-amber-300" : "text-muted-foreground"}`}>
                      <span className="font-mono text-[10px] font-bold uppercase tracking-wider">{DAY_NAMES[idx]}</span>
                      <span className={`text-sm font-bold ${isToday ? "text-amber-300" : "text-foreground"}`}>{d.getDate()}</span>
                    </div>
                    <div className="space-y-1">
                      {list.map((t) => <CalChip key={t.id} t={t} />)}
                      <button data-testid={`crm-cal-add-${key}`} onClick={() => setEdit({ ...EMPTY, due_date: key })} className="w-full rounded-md border border-dashed border-border py-1 text-[11px] text-muted-foreground transition-colors hover:border-amber-400 hover:text-amber-300">+ dodaj</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {view === "list" && <>
      {BUCKETS.filter((b) => b.k !== "done" || showDone).map((b) => {
        const list = grouped[b.k];
        if (!list.length) return null;
        return (
          <div key={b.k} data-testid={`crm-bucket-${b.k}`}>
            <div className={`mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest ${b.c}`}>
              <b.icon className="h-4 w-4" />{b.l} ({list.length})
            </div>
            <div className="space-y-2">{list.map((t) => <TaskRow key={t.id} t={t} />)}</div>
          </div>
        );
      })}
      {!tasks.filter((t) => showDone || t.status !== "done").length && (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">Brak zadań. Dodaj pierwsze zadanie, aby zaplanować pracę.</div>
      )}
      </>}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="crm-task-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">{edit?.id ? "Edytuj zadanie" : "Nowe zadanie"}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <div><Label className="text-xs">Tytuł *</Label><Input data-testid="crm-task-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="mt-1" placeholder="np. Zadzwonić w sprawie oferty M18" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Typ</Label>
                <Select value={edit.type} onValueChange={(v) => setEdit({ ...edit, type: v })}>
                  <SelectTrigger data-testid="crm-task-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Priorytet</Label>
                <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
                  <SelectTrigger data-testid="crm-task-priority" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Niski</SelectItem><SelectItem value="normal">Normalny</SelectItem><SelectItem value="high">Wysoki</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Termin</Label><Input data-testid="crm-task-date" type="date" value={edit.due_date || ""} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Godzina</Label><Input data-testid="crm-task-time" type="time" value={edit.due_time || ""} onChange={(e) => setEdit({ ...edit, due_time: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Klient (opcjonalnie)</Label>
              <Select value={edit.customer_id || "none"} onValueChange={(v) => setEdit({ ...edit, customer_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="crm-task-customer" className="mt-1"><SelectValue placeholder="— brak —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— brak —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div><Label className="text-xs">Przypisz do handlowca</Label>
                <Select value={edit.employee_id || "me"} onValueChange={(v) => setEdit({ ...edit, employee_id: v === "me" ? "" : v })}>
                  <SelectTrigger data-testid="crm-task-employee" className="mt-1"><SelectValue placeholder="Ja" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Ja (admin)</SelectItem>
                    {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs">Notatki</Label><Textarea data-testid="crm-task-notes" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="mt-1" rows={3} /></div>
          </div>}
          <DialogFooter>
            <Button data-testid="crm-task-save" onClick={save} disabled={busy} className="bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? "Zapisywanie…" : "Zapisz"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
